import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { imageAnalysisService } from '../../services/aiService';

function ImageAnalysis() {
  const [uploadedImages, setUploadedImages] = useState([]);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const data = await imageAnalysisService.uploadImage(file);
      toast.success(`Bild "${data.image.filename}" erfolgreich hochgeladen`);
      setUploadedImages((prev) => [...prev, data.image]);
      event.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Fehler beim Upload des Bildes');
    } finally {
      setIsUploading(false);
    }
  };

  const analyzeImage = async (imageId, analysisType) => {
    setIsAnalyzing(true);
    try {
      const data = await imageAnalysisService.analyzeImage(imageId, analysisType);
      setAnalysisResults((prev) => [...prev, data.analysis]);
      toast.success(
        `Bildanalyse abgeschlossen mit ${Math.round(data.analysis.results.confidence * 100)}% Vertrauen`,
      );
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Fehler bei der Bildanalyse');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getFindingTypeColor = (type) => {
    switch (type) {
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'info':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getFindingIcon = (type) => {
    switch (type) {
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      case 'info':
        return 'ℹ️';
      default:
        return '🔍';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  🔍 Bildanalyse
                </h1>
                <p className="mt-2 text-gray-600">
                  KI-gestützte Analyse von Gerätebildern und Röntgenaufnahmen
                </p>
              </div>
              <button
                onClick={() => window.history.back()}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md"
              >
                ← Zurück
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upload & Images Section */}
          <div className="space-y-6">
            {/* Upload Area */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Bild hochladen</h2>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">🖼️</div>
                <p className="text-gray-600 mb-4">
                  Gerätefotos, Röntgenbilder oder Display-Screenshots hochladen
                </p>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.tiff"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                  disabled={isUploading}
                />
                <label
                  htmlFor="image-upload"
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                    isUploading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                  }`}
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Wird hochgeladen...
                    </>
                  ) : (
                    'Bild auswählen'
                  )}
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  Unterstützte Formate: JPG, PNG, TIFF (max. 25MB)
                </p>
              </div>
            </div>

            {/* Uploaded Images */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Hochgeladene Bilder</h2>
              </div>
              <div className="p-6">
                {uploadedImages.length > 0 ? (
                  <div className="space-y-4">
                    {uploadedImages.map((image) => (
                      <div key={image.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">🖼️</div>
                            <div>
                              <p className="font-medium text-gray-900">{image.filename}</p>
                              <p className="text-sm text-gray-500">
                                {image.imageType} • {(image.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              image.status === 'processing'
                                ? 'bg-yellow-100 text-yellow-800'
                                : image.status === 'completed'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {image.status === 'processing'
                              ? 'Verarbeitung'
                              : image.status === 'completed'
                                ? 'Fertig'
                                : 'Unbekannt'}
                          </span>
                        </div>

                        {image.description && (
                          <p className="text-sm text-gray-600 mb-3">{image.description}</p>
                        )}

                        {/* Analysis Buttons */}
                        <div className="flex space-x-2">
                          <button
                            onClick={() => analyzeImage(image.id, 'defect_detection')}
                            disabled={isAnalyzing}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs"
                          >
                            Defekt-Erkennung
                          </button>
                          <button
                            onClick={() => analyzeImage(image.id, 'performance_check')}
                            disabled={isAnalyzing}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs"
                          >
                            Performance-Check
                          </button>
                          <button
                            onClick={() => analyzeImage(image.id, 'safety_check')}
                            disabled={isAnalyzing}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs"
                          >
                            Sicherheits-Check
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">📷</div>
                    <p>Noch keine Bilder hochgeladen</p>
                    <p className="text-sm">Laden Sie Bilder hoch, um mit der Analyse zu beginnen</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Analysis Results Section */}
          <div className="space-y-6">
            {/* Analysis Types Info */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Analyse-Typen</h2>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="text-xl">❌</div>
                  <div>
                    <h3 className="font-medium text-gray-900">Defekt-Erkennung</h3>
                    <p className="text-sm text-gray-600">
                      Erkennt Schäden, Verschleiß und Defekte an Geräten
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="text-xl">📊</div>
                  <div>
                    <h3 className="font-medium text-gray-900">Performance-Check</h3>
                    <p className="text-sm text-gray-600">Bewertet die Leistung und Bildqualität</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="text-xl">🛡️</div>
                  <div>
                    <h3 className="font-medium text-gray-900">Sicherheits-Check</h3>
                    <p className="text-sm text-gray-600">
                      Überprüft Sicherheitsaspekte und Compliance
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Analysis Results */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Analyse-Ergebnisse</h2>
              </div>
              <div className="p-6">
                {analysisResults.length > 0 ? (
                  <div className="space-y-6">
                    {analysisResults.map((result) => (
                      <div key={result.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium text-gray-900">Analyse #{result.id}</h3>
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                            {Math.round(result.results.confidence * 100)}% Vertrauen
                          </span>
                        </div>

                        {/* Findings */}
                        {result.results.findings && result.results.findings.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-medium text-gray-900 mb-2">Erkenntnisse:</h4>
                            <div className="space-y-2">
                              {result.results.findings.map((finding, index) => (
                                <div
                                  key={index}
                                  className={`border rounded-md p-3 ${getFindingTypeColor(finding.type)}`}
                                >
                                  <div className="flex items-start space-x-2">
                                    <span className="text-lg">{getFindingIcon(finding.type)}</span>
                                    <div className="flex-1">
                                      <p className="font-medium">{finding.description}</p>
                                      {finding.coordinates && (
                                        <p className="text-xs mt-1 opacity-75">
                                          Position: x:{finding.coordinates.x}, y:
                                          {finding.coordinates.y}({finding.coordinates.width}×
                                          {finding.coordinates.height})
                                        </p>
                                      )}
                                      <p className="text-xs mt-1 opacity-75">
                                        Vertrauen: {Math.round(finding.confidence * 100)}%
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recommendation */}
                        {result.results.recommendation && (
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <h4 className="font-medium text-blue-900 mb-1">Empfehlung:</h4>
                            <p className="text-blue-800 text-sm">{result.results.recommendation}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">🔍</div>
                    <p>Noch keine Analysen durchgeführt</p>
                    <p className="text-sm">Laden Sie Bilder hoch und starten Sie eine Analyse</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Schnellaktionen</h2>
              <div className="space-y-2">
                <button
                  type="button"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm"
                >
                  📊 Alle Ergebnisse exportieren
                </button>
                <button
                  type="button"
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md text-sm"
                >
                  📋 Bericht generieren
                </button>
                <button
                  type="button"
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-md text-sm"
                >
                  🗑️ Alle Daten löschen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageAnalysis;
